package org.moxultimate.topwithmox.tech.ui

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.cards.ContentCard
import org.moxultimate.topwithmox.components.buttons.PressableButtonStyle
import org.moxultimate.topwithmox.components.chip.Chip
import org.moxultimate.topwithmox.components.chip.ChipRowScrollStyle
import org.moxultimate.topwithmox.components.error.ErrorPage
import org.moxultimate.topwithmox.components.loading.LoadingIndicator
import org.moxultimate.topwithmox.components.loading.LoadingMoreIndicator
import org.moxultimate.topwithmox.components.loading.OverlayLoadingIndicator
import org.moxultimate.topwithmox.components.searchbar.SearchBarWithAutocomplete
import org.moxultimate.topwithmox.components.texts.PageHeading
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.statics.TechStatics
import org.moxultimate.topwithmox.tech.data.TechResponse
import org.moxultimate.topwithmox.theme.*

/**
 * TechPage
 * Entry composable for the /tech route.
 * Observes [TechViewModel] state and delegates rendering to [TechListPage].
 */
@Composable
fun TechPage() {
    val viewModel: TechViewModel = koinViewModel()
    val screenState by viewModel.screenState.collectAsState()
    val isLoadingMore by viewModel.isLoadingMore.collectAsState()
    val hasMore by viewModel.hasMore.collectAsState()
    val allTypes by viewModel.allTypes.collectAsState()
    val allTags by viewModel.allTags.collectAsState()
    val selectedTypes by viewModel.selectedTypes.collectAsState()
    val searchText by viewModel.searchText.collectAsState()

    var lastSuccess by remember { mutableStateOf<TechState.Success?>(null) }
    if (screenState is TechState.Success) {
        lastSuccess = screenState as TechState.Success
    }

    val isFilterReloading = screenState is TechState.Loading && lastSuccess != null

    Box(
        Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .backgroundColor(WhiteBG)
            .overflow(Overflow.Hidden),
        contentAlignment = Alignment.Center,
    ) {
        when (val state = screenState) {
            is TechState.Loading -> {
                val cached = lastSuccess
                if (cached != null) {
                    TechListPage(
                        techs = cached.techList,
                        allTypes = allTypes,
                        allTags = allTags,
                        selectedTypes = selectedTypes,
                        searchText = searchText,
                        isLoadingMore = isLoadingMore,
                        hasMore = hasMore,
                        onTypeToggle = { viewModel.toggleType(it) },
                        onSearchChange = { viewModel.setSearchText(it) },
                        onClearFilters = { viewModel.clearAllFilters() },
                        onLoadMore = { viewModel.loadMoreIfNeeded() },
                    )
                } else {
                    LoadingIndicator()
                }
            }

            is TechState.Error -> ErrorPage(
                errorCode = state.statusCode.toString(),
                error = state.message,
                onTryAgain = { viewModel.loadTechs() },
            )

            is TechState.Success -> TechListPage(
                techs = state.techList,
                allTypes = allTypes,
                allTags = allTags,
                selectedTypes = selectedTypes,
                searchText = searchText,
                isLoadingMore = isLoadingMore,
                hasMore = hasMore,
                onTypeToggle = { viewModel.toggleType(it) },
                onSearchChange = { viewModel.setSearchText(it) },
                onClearFilters = { viewModel.clearAllFilters() },
                onLoadMore = { viewModel.loadMoreIfNeeded() },
            )
        }

        OverlayLoadingIndicator(visible = isFilterReloading)
    }
}

/**
 * TechListPage
 * Full scrollable layout: page heading, search bar, type chips, then a 3-column grid of tech cards.
 * Attaches an infinite-scroll listener that triggers [onLoadMore].
 *
 * @param techs         Accumulated (and optionally filtered) list of tech entries.
 * @param allTypes      Full type list for the scrollable chip row.
 * @param selectedTypes Currently active type filter chips.
 * @param searchText    Live text filter applied to tech title and tags.
 * @param isLoadingMore True while the next pagination page is being fetched.
 * @param hasMore       False when all pages have been loaded.
 * @param onTypeToggle  Toggles a type chip on/off.
 * @param onSearchChange Updates [searchText] and re-filters the tech list.
 * @param onClearFilters Resets all chips and search text.
 * @param onLoadMore    Triggered by the scroll handler to fetch the next page.
 */
@OptIn(DelicateApi::class)
@Composable
private fun TechListPage(
    techs: List<TechResponse>,
    allTypes: List<String>,
    allTags: List<String>,
    selectedTypes: Set<String>,
    searchText: String,
    isLoadingMore: Boolean,
    hasMore: Boolean,
    onTypeToggle: (String) -> Unit,
    onSearchChange: (String) -> Unit,
    onClearFilters: () -> Unit,
    onLoadMore: () -> Unit,
) {
    DisposableEffect(hasMore) {
        val handler: (org.w3c.dom.events.Event) -> Unit = {
            val scrolled = window.scrollY + window.innerHeight
            val total = window.document.documentElement?.scrollHeight ?: 0
            if (hasMore && scrolled >= total - 300) onLoadMore()
        }
        window.addEventListener("scroll", handler)
        onDispose { window.removeEventListener("scroll", handler) }
    }

    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint <= Breakpoint.SM
    val hasActiveFilters = selectedTypes.isNotEmpty() || searchText.isNotBlank()

    Column(
        modifier = if (isMobile)
            Modifier.fillMaxWidth().padding(leftRight = 16.px)
        else
            Modifier
                .width(clamp(min = 1200.px, value = 90.vw, max = 2000.px))
                .padding(leftRight = 3.cssRem),
    ) {
        PageHeading(
            subtitle = TechStatics.PAGE_SUBTITLE,
            titleLine1 = TechStatics.PAGE_TITLE_LINE1,
            titleLine2 = TechStatics.PAGE_TITLE_LINE2,
            description = TechStatics.PAGE_DESCRIPTION,
            detail = TechStatics.PAGE_DETAIL_1,
            detailType1 = TechStatics.PAGE_DETAIL_TYPE1,
            detail2 = TechStatics.PAGE_DETAIL_2,
            detailType2 = TechStatics.PAGE_DETAIL_TYPE2,
            detail3 = TechStatics.PAGE_DETAIL_3,
            detailType3 = TechStatics.PAGE_DETAIL_TYPE3,
        )

        Column(
            modifier = Modifier.fillMaxWidth().backgroundColor(Colors.White),
        ) {
            // Search bar row — search input + optional Clear Filters button
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.px),
            ) {
                Box(modifier = Modifier.flexGrow(1)) {
                    SearchBarWithAutocomplete(
                        value = searchText,
                        onValueChange = onSearchChange,
                        suggestions = allTags,
                        placeholder = TechStatics.SEARCH_PLACEHOLDER,
                        onSearch = onSearchChange,
                    )
                }

                if (hasActiveFilters) {
                    Row(
                        modifier = PressableButtonStyle.toModifier()
                            .padding(left = 14.px, top = 8.px, bottom = 6.px, right = 12.px)
                            .borderRadius(16.px)
                            .boxShadow(blurRadius = 2.px, color = Colors.LightGray)
                            .styleModifier { property("background", "#FFFFFF") }
                            .onClick { onClearFilters() },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        SpanText(
                            text = "Clear Filters",
                            modifier = Modifier
                                .fontFamily(FontFamily)
                                .fontSize(clamp(min = MinChipFontSize, value = 0.8.vw, max = MaxChipFontSize))
                                .moxTextGradient(),
                        )
                    }
                }
            }

            // Type chips
            if (allTypes.isNotEmpty()) {
                Row(
                    modifier = ChipRowScrollStyle.toModifier()
                        .fillMaxWidth()
                        .padding(top = 12.px, bottom = 4.px),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.px),
                ) {
                    allTypes.forEach { type ->
                        Chip(
                            description = type,
                            isSelected = type in selectedTypes,
                            onChipClick = { onTypeToggle(type) },
                        )
                    }
                }
            }
        }

        // 3-column flex-wrap grid of tech cards
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(topBottom = 30.px)
                .styleModifier {
                    property("flex-wrap", "wrap")
                    property("align-items", "stretch")
                },
            horizontalArrangement = Arrangement.spacedBy(24.px),
        ) {
            techs.forEach { tech ->
                TechCard(
                    tech = tech,
                    modifier = if (isMobile)
                        Modifier.fillMaxWidth().margin(bottom = 24.px)
                    else
                        Modifier
                            .styleModifier { property("width", "calc((100% - 48px) / 3)") }
                            .margin(bottom = 24.px),
                )
            }
        }

        if (isLoadingMore) {
            Box(
                modifier = Modifier.fillMaxWidth().padding(top = 12.px, bottom = 24.px),
                contentAlignment = Alignment.Center,
            ) {
                LoadingMoreIndicator()
            }
        }
    }
}

/**
 * TechCard
 * Delegates to [ContentCard] using tech-specific data and a "Watch Video" action button.
 *
 * @param tech Tech data to render.
 * @param modifier Outer modifier — caller controls width and margin for grid placement.
 */
@Composable
private fun TechCard(
    tech: TechResponse,
    modifier: Modifier = Modifier,
) {
    ContentCard(
        imageSrc = tech.thumbnailPath.orEmpty(),
        type = tech.type,
        title = tech.title.orEmpty(),
        description = tech.description,
        modifier = modifier,
        actions = {
            if (!tech.videoLink.isNullOrBlank()) {
                GradientButton(
                    text = TechStatics.CARD_WATCH_VIDEO,
                    buttonHeight = 42,
                    onClick = { window.open(tech.videoLink, "_blank") },
                )
            }
        },
    )
}
